import Phaser from "phaser";
import { CameraGazeAdapter } from "../adapters/CameraGazeAdapter";
import { GazeCursor } from "../entities/GazeCursor";
import { HUD } from "../ui/HUD";
import { createInitialRunState, RunState } from "../state/RunState";
import { EMA } from "../utils/EMA";
import {
  EMA_ALPHA,
  EMA_INITIAL_DELTA,
  GAME_WIDTH,
  GAME_HEIGHT,
} from "../app/Config";

import { GazeSystem } from "../systems/GazeSystem";
import { TargetSystem } from "../systems/TargetSystem";
import { ObjectSystem } from "../systems/ObjectSystem";
import { CollisionSystem } from "../systems/CollisionSystem";
import { ScoreSystem } from "../systems/ScoreSystem";
import { StageSystem } from "../systems/StageSystem";
import { TimerSystem } from "../systems/TimerSystem";
import { FXSystem } from "../systems/FXSystem";
import { StageConfigEntry } from "../data/StageConfig";

export class PlayScene extends Phaser.Scene {
  private runState!: RunState;

  private gazeSystem!: GazeSystem;
  private targetSystem!: TargetSystem;
  private objectSystem!: ObjectSystem;
  private collisionSystem!: CollisionSystem;
  private scoreSystem!: ScoreSystem;
  private stageSystem!: StageSystem;
  private timerSystem!: TimerSystem;
  private fxSystem!: FXSystem;

  private hud!: HUD;
  private stageConfig!: StageConfigEntry;

  constructor() {
    super({ key: "PlayScene" });
  }

  init(data?: { isBoostActive?: boolean }): void {
    this.runState = createInitialRunState();
    if (data?.isBoostActive) {
      this.runState.isBoostActive = true;
    }
  }

  create(): void {
    const adapter = new CameraGazeAdapter(this);
    const cursor = new GazeCursor(this);
    const emaDelta = new EMA(EMA_ALPHA, EMA_INITIAL_DELTA);

    this.gazeSystem = new GazeSystem(adapter, cursor);
    this.targetSystem = new TargetSystem(this);
    this.objectSystem = new ObjectSystem(this);
    this.collisionSystem = new CollisionSystem();
    this.scoreSystem = new ScoreSystem(emaDelta);
    this.stageSystem = new StageSystem();
    this.timerSystem = new TimerSystem();
    this.fxSystem = new FXSystem(
      this,
      "default",
      this.runState.isBoostActive
    );

    this.gazeSystem.start();
    this.loadStage();
    this.hud = new HUD(this);
  }

  private loadStage(): void {
    this.stageConfig = this.stageSystem.getCurrentConfig(this.runState);
    this.stageSystem.initStageTimer(this.runState);

    this.targetSystem.createTarget(this.stageConfig, GAME_WIDTH, GAME_HEIGHT);
    this.objectSystem.createObjects(
      this.stageConfig.objectCount,
      GAME_WIDTH,
      GAME_HEIGHT
    );
  }

  update(_time: number, delta: number): void {
    const dt = delta / 1000;

    // Gaze
    const gaze = this.gazeSystem.update();

    // Target movement
    this.targetSystem.update(dt);

    // Object tracking
    this.objectSystem.updateActiveObject(gaze);

    // Collision
    const target = this.targetSystem.getTarget();
    if (target) {
      const hits = this.collisionSystem.checkHits(
        this.objectSystem.getObjects(),
        target
      );
      for (const hit of hits) {
        hit.object.hasScored = true;

        const nowMs = this.timerSystem.getElapsedMs();
        const addScore = this.scoreSystem.calculateAndApply(
          this.runState,
          hit.dist,
          target.radius,
          nowMs
        );

        target.flashSuccess();
        this.fxSystem.playHitEffect(hit.object.x, hit.object.y, addScore);

        // Stage promotion
        if (this.stageSystem.checkPromotion(this.runState)) {
          this.loadStage();
          return;
        }

        // Respawn
        this.objectSystem.respawn(hit.object, GAME_WIDTH, GAME_HEIGHT);
      }
    }

    // Timer & combo decay
    const timeUp = this.timerSystem.update(this.runState, delta);
    if (timeUp) {
      this.onTimeUp();
      return;
    }

    // HUD
    this.hud.update(
      this.runState.score,
      this.runState.combo,
      this.runState.timeRemainingMs / 1000,
      this.runState.hits,
      this.stageConfig.requiredHits,
      this.runState.stageIndex
    );
  }

  private onTimeUp(): void {
    this.gazeSystem.stop();
    this.scene.start("ResultScene", {
      score: this.runState.score,
      hits: this.runState.hits,
      stageIndex: this.runState.stageIndex,
    });
  }

  shutdown(): void {
    this.gazeSystem?.destroy();
    this.targetSystem?.destroy();
    this.objectSystem?.destroyAll();
    this.hud?.destroy();
  }
}
